import { memo, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tag } from '@carbon/react';
import {
  Idea,
  Locked,
  Unlocked,
  MachineLearningModel,
  TrashCan,
  Connect,
} from '@carbon/icons-react';

/** The three provenance states for a node or edge. */
export type NodeProvenance = 'ai' | 'user' | 'corrected';

export interface ConceptNodeData extends Record<string, unknown> {
  label: string;
  definition: string;
  properties: (string | { name: string; type?: string })[];
  locked?: boolean;
  isReadOnly?: boolean;
  /** Who authored/last-edited this concept (A9): 'ai' (generated) or 'user'
   *  (manually added, or an AI concept the user corrected). */
  origin?: 'ai' | 'user';
  /** Provenance — 'ai' (default), 'user' (human-created), 'corrected'
   *  (AI-generated node later edited by a human). Supersedes `origin`. */
  provenance?: NodeProvenance;
  /** Display name / email of the original creator (null = AI-generated). */
  createdByName?: string | null;
  /** Display name / email of the last human editor. */
  updatedByName?: string | null;
  /** ISO timestamp of the first human correction (set once). */
  correctedAt?: string | null;
}

interface ConceptNodeProps extends NodeProps {
  onExpandConcept?: (nodeId: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  onLockNode?: (nodeId: string) => void;
  onUnlockNode?: (nodeId: string) => void;
  onLinkConcept?: (nodeId: string) => void;
}

const ConceptNodeInner = ({
  data,
  selected,
  id,
  onExpandConcept,
  onDeleteNode,
  onLockNode,
  onUnlockNode,
  onLinkConcept,
}: ConceptNodeProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const nodeData = data as ConceptNodeData;
  const expandCb = (data as any)?.onExpandConcept || onExpandConcept;
  const deleteCb = (data as any)?.onDeleteNode || onDeleteNode;
  const lockCb = (data as any)?.onLockNode || onLockNode;
  const unlockCb = (data as any)?.onUnlockNode || onUnlockNode;
  const linkCb = (data as any)?.onLinkConcept || onLinkConcept;

  const isReadOnly = !!nodeData.isReadOnly;
  const showActions = selected || isHovered;
  // Provenance drives the badge; fall back to the legacy `origin` flag so
  // graphs saved before the provenance columns existed still render a badge.
  const provenance: NodeProvenance =
    nodeData.provenance ?? (nodeData.origin === 'user' ? 'user' : 'ai');

  return (
    <>
      {/* Connection handles - positioned on all 4 sides for floating edge connections.
          They become visible on node hover/select via CSS (.concept-node-wrapper:hover .concept-node__handle).
          Source handles sit on top so a drag from any side initiates a connection. */}
      <Handle
        id="target-top"
        type="target"
        position={Position.Top}
        className="concept-node__handle concept-node__handle--target"
      />
      <Handle
        id="target-bottom"
        type="target"
        position={Position.Bottom}
        className="concept-node__handle concept-node__handle--target"
      />
      <Handle
        id="target-left"
        type="target"
        position={Position.Left}
        className="concept-node__handle concept-node__handle--target"
      />
      <Handle
        id="target-right"
        type="target"
        position={Position.Right}
        className="concept-node__handle concept-node__handle--target"
      />

      <Handle
        id="source-top"
        type="source"
        position={Position.Top}
        className="concept-node__handle concept-node__handle--source"
      />
      <Handle
        id="source-bottom"
        type="source"
        position={Position.Bottom}
        className="concept-node__handle concept-node__handle--source"
      />
      <Handle
        id="source-left"
        type="source"
        position={Position.Left}
        className="concept-node__handle concept-node__handle--source"
      />
      <Handle
        id="source-right"
        type="source"
        position={Position.Right}
        className="concept-node__handle concept-node__handle--source"
      />
      
      <div 
        className={`concept-node ${selected ? 'selected' : ''} ${nodeData.locked ? 'locked' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          minWidth: '200px',
          maxWidth: '300px',
          padding: '1rem',
          backgroundColor: 'var(--cds-layer-01)',
          border: selected ? '2px solid var(--cds-focus)' : '1px solid var(--cds-border-subtle-01)',
          borderRadius: '4px',
          boxShadow: selected ? '0 0 0 2px var(--cds-focus)' : '0 2px 6px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          position: 'relative',
        }}
      >
        {provenance === 'user' && (
          <span
            data-testid="concept-origin-user"
            title="Added or edited by you (not AI-generated)"
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: 'var(--cds-interactive-01)',
              color: 'white',
              fontSize: '0.625rem',
              fontWeight: 600,
              lineHeight: 1,
              padding: '0.15rem 0.4rem',
              borderRadius: '0.75rem',
              border: '1px solid var(--cds-layer-01)',
            }}
          >
            You
          </span>
        )}
        {provenance === 'corrected' && (
          <span
            data-testid="concept-origin-corrected"
            title="AI-generated, then corrected by a human"
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: 'var(--cds-support-warning, #f1c21b)',
              color: 'var(--cds-text-on-color-disabled, #161616)',
              fontSize: '0.625rem',
              fontWeight: 600,
              lineHeight: 1,
              padding: '0.15rem 0.4rem',
              borderRadius: '0.75rem',
              border: '1px solid var(--cds-layer-01)',
            }}
          >
            Edited
          </span>
        )}

        <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
          <div style={{ 
            width: '32px', 
            height: '32px', 
            borderRadius: '50%', 
            backgroundColor: nodeData.locked ? 'var(--cds-support-warning)' : 'var(--cds-interactive-01)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {nodeData.locked ? (
              <Locked size={16} style={{ fill: 'white' }} />
            ) : (
              <Idea size={16} style={{ fill: 'white' }} />
            )}
          </div>
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ 
              fontSize: '0.875rem', 
              fontWeight: 600, 
              marginBottom: '0.5rem',
              color: 'var(--cds-text-primary)',
              lineHeight: 1.3
            }}>
              {nodeData.label}
            </h3>
            
            <p style={{ 
              fontSize: '0.75rem', 
              color: 'var(--cds-text-secondary)',
              marginBottom: '0.75rem',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.4
            }}>
              {nodeData.definition}
            </p>
            
            {nodeData.properties && nodeData.properties.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {nodeData.properties.slice(0, 3).map((property, index) => {
                  const displayText = typeof property === 'string' 
                    ? property 
                    : property.type 
                      ? `${property.name}: ${property.type}`
                      : property.name;
                  
                  return (
                    <Tag key={index} type="blue" size="sm">
                      {displayText}
                    </Tag>
                  );
                })}
                {nodeData.properties.length > 3 && (
                  <Tag type="outline" size="sm">
                    +{nodeData.properties.length - 3}
                  </Tag>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action buttons - top right corner, visible on hover or when selected.
            Hidden entirely in read-only mode — the ontology cannot be modified. */}
        {!isReadOnly && (
        <div 
          className="node-actions"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            gap: '6px',
            opacity: showActions ? 1 : 0,
            pointerEvents: showActions ? 'auto' : 'none',
            transition: 'opacity 0.2s ease',
          }}
        >
          {nodeData.locked ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                unlockCb?.(id!);
              }}
              title="Unlock node"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '4px',
                border: '1px solid var(--cds-border-subtle-01)',
                backgroundColor: 'var(--cds-layer-02)',
                color: 'var(--cds-support-warning)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              }}
            >
              <Unlocked size={14} />
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                lockCb?.(id!);
              }}
              title="Lock node position"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '4px',
                border: '1px solid var(--cds-border-subtle-01)',
                backgroundColor: 'var(--cds-layer-02)',
                color: 'var(--cds-icon-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--cds-support-warning)';
                (e.currentTarget as HTMLButtonElement).style.color = 'white';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--cds-layer-02)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--cds-icon-primary)';
              }}
            >
              <Locked size={14} />
            </button>
          )}
          {/* Link concept — opens the ConceptLinker modal as an alternative
              to the drag-to-link gesture. Useful when the target node is
              off-screen or the user prefers a guided form-based flow. */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              linkCb?.(id!);
            }}
            title="Link to another concept"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '4px',
              border: '1px solid var(--cds-border-subtle-01)',
              backgroundColor: 'var(--cds-layer-02)',
              color: 'var(--cds-icon-primary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                'var(--cds-purple-60, #8a3ffc)';
              (e.currentTarget as HTMLButtonElement).style.color = 'white';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                'var(--cds-layer-02)';
              (e.currentTarget as HTMLButtonElement).style.color =
                'var(--cds-icon-primary)';
            }}
          >
            <Connect size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              expandCb?.(id!);
            }}
            title="Expand with AI"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '4px',
              border: '1px solid var(--cds-border-subtle-01)',
              backgroundColor: 'var(--cds-layer-02)',
              color: 'var(--cds-icon-primary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--cds-interactive-01)';
              (e.currentTarget as HTMLButtonElement).style.color = 'white';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--cds-layer-02)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--cds-icon-primary)';
            }}
          >
            <MachineLearningModel size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteCb?.(id!);
            }}
            title="Remove node and relationships"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '4px',
              border: '1px solid var(--cds-border-subtle-01)',
              backgroundColor: 'var(--cds-layer-02)',
              color: 'var(--cds-icon-primary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--cds-support-error)';
              (e.currentTarget as HTMLButtonElement).style.color = 'white';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--cds-layer-02)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--cds-icon-primary)';
            }}
          >
            <TrashCan size={14} />
          </button>
        </div>
        )}
      </div>
    </>
  );
};

/**
 * Custom equality check for React.memo. Only re-render when the data the
 * node actually displays has changed — ignore position updates, internal
 * React Flow bookkeeping, etc. This is the single biggest perf win for
 * graphs with 50+ nodes: without it every node re-renders on every pan/
 * zoom/drag frame.
 */
function areConceptNodePropsEqual(
  prev: ConceptNodeProps,
  next: ConceptNodeProps,
): boolean {
  if (prev.id !== next.id) return false;
  if (prev.selected !== next.selected) return false;

  const prevData = prev.data as ConceptNodeData;
  const nextData = next.data as ConceptNodeData;
  if (prevData.label !== nextData.label) return false;
  if (prevData.definition !== nextData.definition) return false;
  if (prevData.locked !== nextData.locked) return false;
  if (prevData.isReadOnly !== nextData.isReadOnly) return false;
  if (prevData.properties !== nextData.properties) return false;
  if (prevData.origin !== nextData.origin) return false;
  if (prevData.provenance !== nextData.provenance) return false;
  if (prevData.updatedByName !== nextData.updatedByName) return false;

  return true;
}

export const ConceptNode = memo(ConceptNodeInner, areConceptNodePropsEqual);
